import type { ChallengeVideoRule } from '../config/tripChallenges'

export const MAX_CHALLENGE_VIDEO_BYTES = 80 * 1024 * 1024
export const CHALLENGE_VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm,video/x-m4v,video/3gpp'

const acceptedVideoMimes = new Set([
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-m4v',
  'video/3gpp',
])

export type PreparedChallengeVideo = Readonly<{
  file: File
  durationSeconds: number
  thumbnail: Blob | null
}>

export type PreparedAlbumVideo = PreparedChallengeVideo

export function validateVideoMetadata(
  input: Readonly<{ type: string; size: number; durationSeconds: number }>,
  rule: ChallengeVideoRule,
): void {
  if (!acceptedVideoMimes.has(input.type) || input.size <= 0) {
    throw new Error('Choisis une vidéo MP4, MOV, WebM, M4V ou 3GP lisible.')
  }
  if (input.size > MAX_CHALLENGE_VIDEO_BYTES) {
    throw new Error('Cette vidéo dépasse 80 Mo. Raccourcis-la ou réduis sa qualité avant de réessayer.')
  }
  if (!Number.isFinite(input.durationSeconds) || input.durationSeconds <= 0) {
    throw new Error('La durée de cette vidéo est illisible. Essaie une autre vidéo ou exporte-la en MP4.')
  }
  if (rule.minDurationSeconds !== undefined && input.durationSeconds < rule.minDurationSeconds) {
    throw new Error(`Cette vidéo doit durer au moins ${String(rule.minDurationSeconds)} secondes.`)
  }
  if (input.durationSeconds > rule.maxDurationSeconds + 0.05) {
    throw new Error(`Cette vidéo dépasse ${String(rule.maxDurationSeconds)} secondes. Raccourcis-la avant de réessayer.`)
  }
}

export async function prepareChallengeVideo(file: File, rule: ChallengeVideoRule): Promise<PreparedChallengeVideo> {
  return prepareVideo(file, rule)
}

export async function prepareAlbumVideo(file: File): Promise<PreparedAlbumVideo> {
  return prepareVideo(file, null)
}

async function prepareVideo(file: File, rule: ChallengeVideoRule | null): Promise<PreparedChallengeVideo> {
  if (!acceptedVideoMimes.has(file.type) || file.size <= 0) {
    throw new Error('Choisis une vidéo MP4, MOV, WebM, M4V ou 3GP lisible.')
  }
  if (file.size > MAX_CHALLENGE_VIDEO_BYTES) {
    throw new Error('Cette vidéo dépasse 80 Mo. Raccourcis-la ou réduis sa qualité avant de réessayer.')
  }

  const url = URL.createObjectURL(file)
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.preload = 'metadata'
  video.src = url
  try {
    const durationSeconds = await readDuration(video)
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
      throw new Error('La durée de cette vidéo est illisible. Essaie une autre vidéo ou exporte-la en MP4.')
    }
    if (rule) validateVideoMetadata({ type: file.type, size: file.size, durationSeconds }, rule)
    const thumbnail = await createThumbnail(video, durationSeconds).catch(() => null)
    return Object.freeze({ file, durationSeconds, thumbnail })
  } finally {
    video.removeAttribute('src')
    video.load()
    URL.revokeObjectURL(url)
  }
}

function readDuration(video: HTMLVideoElement): Promise<number> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => finish(new Error('La lecture des informations de cette vidéo a expiré.')), 12_000)
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener('loadedmetadata', onLoaded)
      video.removeEventListener('error', onError)
    }
    const finish = (error?: Error) => {
      cleanup()
      if (error) reject(error)
      else resolve(video.duration)
    }
    const onLoaded = () => finish()
    const onError = () => finish(new Error('Cette vidéo est illisible sur cet appareil. Essaie une autre vidéo ou exporte-la en MP4.'))
    video.addEventListener('loadedmetadata', onLoaded, { once: true })
    video.addEventListener('error', onError, { once: true })
    video.load()
  })
}

async function createThumbnail(video: HTMLVideoElement, durationSeconds: number): Promise<Blob | null> {
  const target = Math.min(Math.max(durationSeconds / 2, 0.1), 1)
  if (Math.abs(video.currentTime - target) > 0.05) {
    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => finish(new Error('Miniature indisponible.')), 5_000)
      const cleanup = () => {
        window.clearTimeout(timeout)
        video.removeEventListener('seeked', onSeeked)
        video.removeEventListener('error', onError)
      }
      const finish = (error?: Error) => {
        cleanup()
        if (error) reject(error)
        else resolve()
      }
      const onSeeked = () => finish()
      const onError = () => finish(new Error('Miniature indisponible.'))
      video.addEventListener('seeked', onSeeked, { once: true })
      video.addEventListener('error', onError, { once: true })
      video.currentTime = target
    })
  }
  if (!video.videoWidth || !video.videoHeight) return null
  const width = Math.min(720, video.videoWidth)
  const height = Math.max(1, Math.round(width * video.videoHeight / video.videoWidth))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) return null
  context.drawImage(video, 0, 0, width, height)
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.78))
}
